import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { BookOpen, Code, Zap, Globe, Lock, Layers, Lightbulb, Sparkles, Network, Presentation, ChevronDown, ChevronUp } from 'lucide-react';
import HamburgerMenu from './HamburgerMenu';
import { flatlandTrapTwee } from '../data/flatlandTrap';
import { compileFlatlandStory } from '../utils/flatlandCompiler';
import SEO, { generateFAQSchema } from './SEO';
import StoryNav from './StoryNav';

// FAQ Data for structured data and display
const FAQ_DATA = [
  {
    question: "What website builders does TweePorter work with?",
    answer: "TweePorter works with GoDaddy, Wix, Squarespace, Shopify, Webflow, WordPress (HTML blocks), and any website builder that accepts custom HTML. The output is script-free, so it bypasses restrictions that normally block interactive content."
  },
  {
    question: "Do I need coding experience to use TweePorter?",
    answer: "No coding experience required! If you can use Twine (a free visual tool for creating interactive stories), you can use TweePorter. Simply paste your Twee code and get clean HTML output. We also offer a Visual Story Planner with AI assistance for those who prefer not to write code at all."
  },
  {
    question: "Is TweePorter free to use?",
    answer: "Yes, TweePorter is free to use. You can compile unlimited stories and use all basic features at no cost. Some advanced AI-powered features may have usage limits."
  },
  {
    question: "What is Twee/SugarCube format?",
    answer: "Twee is a plain-text format for writing interactive fiction, commonly used with Twine. SugarCube is a popular story format that adds features like variables, macros, and styling. TweePorter is optimized for SugarCube-style Twee code."
  },
  {
    question: "Can I use variables and conditional logic in my stories?",
    answer: "Yes! TweePorter supports SugarCube variables and basic conditional logic. You can create dynamic stories that change based on reader choices, track inventory items, or show different content based on previous decisions."
  },
  {
    question: "How do I embed the compiled story on my website?",
    answer: "After compiling your story, simply copy the generated HTML and paste it into any HTML block or custom code section on your website builder. The story runs entirely within that HTML block—no external files or hosting needed."
  },
  {
    question: "What's the difference between the Compiler and Visual Story Planner?",
    answer: "The Compiler is for users who already have Twee code (from Twine or written manually). The Visual Story Planner is a drag-and-drop interface with AI assistance that helps you create stories from scratch without writing code."
  },
  {
    question: "Can I customize the look of my interactive stories?",
    answer: "Absolutely! TweePorter offers multiple layout options, color customization for text, links, backgrounds, and buttons. You can match your story's appearance to your website's branding."
  },
  {
    question: "Are there limits on story length or complexity?",
    answer: "There are no strict limits, but very large stories (hundreds of passages) may result in larger HTML files. For optimal performance, we recommend keeping stories under 100 passages, though the system can handle more."
  },
  {
    question: "What is the Customer Journey Wizard?",
    answer: "The Customer Journey Wizard is a specialized tool that helps businesses create interactive customer journey maps. It guides you through defining problems, personas, features, and story arcs, then generates an interactive story that demonstrates your product's value proposition."
  }
];

const Landing = () => {
  const navigate = useNavigate();
  const [demoExpanded, setDemoExpanded] = useState(true);
  const [compiledStoryHtml, setCompiledStoryHtml] = useState('');
  const [faqExpanded, setFaqExpanded] = useState(false);
  
  // Collapsible section states
  const [whatThisSolvesExpanded, setWhatThisSolvesExpanded] = useState(false);
  const [whyMattersExpanded, setWhyMattersExpanded] = useState(false);
  const [whatYouCanCreateExpanded, setWhatYouCanCreateExpanded] = useState(false);
  const [howItWorksExpanded, setHowItWorksExpanded] = useState(false);
  const [keyFeaturesExpanded, setKeyFeaturesExpanded] = useState(false);
  const [whyPeopleUseExpanded, setWhyPeopleUseExpanded] = useState(false);
  
  // Individual FAQ item expansion states
  const [expandedFaqs, setExpandedFaqs] = useState({});

  const toggleFaq = (index) => {
    setExpandedFaqs(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Generate FAQ schema for SEO
  const faqSchema = generateFAQSchema(FAQ_DATA);

  // Compile the Flatland story once on component mount
  useEffect(() => {
    const html = compileFlatlandStory(flatlandTrapTwee);
    setCompiledStoryHtml(html);
  }, []);

  // Execute the story JavaScript after HTML is injected
  useEffect(() => {
    if (compiledStoryHtml && demoExpanded) {
      console.log('✅ Flatland: HTML compiled, length:', compiledStoryHtml.length);
      
      // Extract and execute the script from compiled HTML
      const scriptMatch = compiledStoryHtml.match(/<script>([\s\S]*?)<\/script>/);
      if (scriptMatch) {
        console.log('✅ Flatland: Script found, executing...');
        try {
          // Execute the script in the global context
          const scriptContent = scriptMatch[1];
          // Use Function instead of eval for better scoping
          const scriptFunc = new Function(scriptContent);
          scriptFunc();
          console.log('✅ Flatland: Script executed successfully');
        } catch (error) {
          console.error('❌ Flatland: Error executing script:', error);
        }
      } else {
        console.error('❌ Flatland: No script tag found in compiled HTML');
      }
    }
  }, [compiledStoryHtml, demoExpanded]);

  // Scroll to top on initial page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* SEO Meta Tags and Structured Data */}
      <SEO 
        title="TweePorter - Convert Twine Stories to Script-Free HTML | Works on Wix, GoDaddy, Squarespace"
        description="Turn Twine interactive stories into clean HTML that works inside GoDaddy, Wix, Squarespace, and Shopify—no scripts, plugins, or hosting required. Free online tool."
        canonicalUrl="/"
        structuredData={faqSchema}
      />
      
      {/* Header */}
      <header className="container mx-auto px-4 py-6 flex items-center justify-between">
        <div 
          className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('/')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/')}
        >
          <BookOpen className="h-8 w-8 text-indigo-600" />
          <div>
            <span className="text-2xl font-bold text-slate-900">TweePorter.ca</span>
            <div className="flex items-center space-x-1 text-xs text-indigo-600 font-medium">
              <Sparkles className="h-3 w-3" />
              <span>Optimized for SugarCube</span>
            </div>
          </div>
        </div>
        <HamburgerMenu />
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            Bring Interactive Stories to Any Website Builder
          </h1>
          <p className="text-xl text-slate-600 mb-0 leading-relaxed">
            Turn static content into immersive "choose your path" experiences that work inside GoDaddy, Wix, Squarespace, Shopify, and other restrictive website builders—without needing custom code, plugins, or hosting.
          </p>
        </div>
      </section>

      {/* Interactive Story Demo - Collapsible */}
      <section className="container mx-auto px-4 pt-4 pb-8 bg-gradient-to-b from-orange-50 to-orange-100">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setDemoExpanded(!demoExpanded)}
            className="w-full flex items-center justify-between bg-white rounded-lg shadow-md px-6 py-4 hover:shadow-lg transition-shadow border-2 border-orange-300"
            type="button"
          >
            <span className="text-2xl font-bold text-slate-900">See It In Action (inline)</span>
            {demoExpanded ? (
              <ChevronUp className="h-6 w-6 text-orange-600" />
            ) : (
              <ChevronDown className="h-6 w-6 text-orange-600" />
            )}
          </button>
          
          {demoExpanded && (
            <div className="mt-4 bg-white rounded-xl shadow-lg overflow-hidden border-2 border-orange-300">
              <div className="bg-gradient-to-r from-orange-400 to-amber-400 text-slate-900 px-6 py-4">
                <h1 className="text-xl font-bold text-center">The Flatland Trap: A Skeptic's Journey</h1>
                <p className="text-center text-sm mt-2 text-slate-700 italic">
                  See how interactive content embeds directly in any website — no iframe needed
                </p>
                {/* Navigation Controls */}
                <div className="flex items-center justify-center gap-4 mt-3 text-sm">
                  <button 
                    onClick={() => window.flatlandGoBack && window.flatlandGoBack()}
                    className="px-3 py-1 rounded text-slate-800 hover:bg-orange-300 transition-colors"
                  >
                    ← Back
                  </button>
                  <span className="text-slate-700">|</span>
                  <button 
                    onClick={() => window.flatlandReset && window.flatlandReset()}
                    className="px-3 py-1 rounded text-slate-800 hover:bg-orange-300 transition-colors"
                  >
                    ⟲ Reset
                  </button>
                </div>
              </div>
              
              {/* Compiled Flatland Story - Inline HTML */}
              <div 
                className="px-6 py-8"
                dangerouslySetInnerHTML={{ __html: compiledStoryHtml }}
              />
            </div>
          )}
        </div>
      </section>

      {/* What This Solves - Now Collapsible */}
      {/* What This Solves - Hybrid Card + Accordion */}
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-md border-2 border-indigo-300 overflow-hidden">
            {/* Always visible card header with summary */}
            <div className="px-6 py-5">
              <h2 className="text-2xl font-bold text-slate-900 mb-3">What This Solves</h2>
              <p className="text-slate-600 leading-relaxed">
                Create interactive stories that work on any website. Three tiers to match your needs: <strong>DIY</strong> for hobbyists, <strong>PRO</strong> for serious creators, and <strong>Agency</strong> for teams building client experiences.
              </p>
            </div>
            
            {/* Expandable details - always in DOM for SEO */}
            <div 
              className={`transition-all duration-300 ease-in-out overflow-hidden ${
                whatThisSolvesExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
              }`}
              aria-hidden={!whatThisSolvesExpanded}
            >
              <div className="px-6 pb-5 pt-2 border-t border-slate-100 space-y-4">
                {/* DIY Tier */}
                <div className="flex gap-3">
                  <span className="px-2 py-1 bg-slate-500 text-white text-xs font-bold rounded h-fit">DIY</span>
                  <div>
                    <p className="text-slate-700 font-medium">Interactive Story DIY</p>
                    <p className="text-slate-600 text-sm">Browse sample stories, learn the format, and use our Easy Story Builder (Compiler) to create script-free HTML that works on GoDaddy, Wix, Squarespace, and more.</p>
                  </div>
                </div>
                
                {/* PRO Tier */}
                <div className="flex gap-3">
                  <span className="px-2 py-1 bg-purple-600 text-white text-xs font-bold rounded h-fit">PRO</span>
                  <div>
                    <p className="text-slate-700 font-medium">Professional Tools</p>
                    <p className="text-slate-600 text-sm">Visual Editor for node-based story building, Visual Story Planner for complex narratives, Shared Story sessions for live multi-user experiences, plus premium export formats.</p>
                  </div>
                </div>
                
                {/* Agency Tier */}
                <div className="flex gap-3">
                  <span className="px-2 py-1 bg-amber-600 text-white text-xs font-bold rounded h-fit">AGENCY</span>
                  <div>
                    <p className="text-slate-700 font-medium">Agency Suite</p>
                    <p className="text-slate-600 text-sm">Everything in PRO, plus Sitemap to PathNav for converting websites into interactive navigation maps, Customer Journey Wizard for client projects, and upcoming enterprise features.</p>
                  </div>
                </div>
                
                <p className="text-slate-500 text-sm italic pt-2 border-t border-slate-100">
                  All tiers produce clean, script-free HTML—no external hosting or dependencies required.
                </p>
              </div>
            </div>
            
            {/* Expand/collapse button */}
            <button
              onClick={() => setWhatThisSolvesExpanded(!whatThisSolvesExpanded)}
              className="w-full flex items-center justify-center gap-2 py-3 bg-indigo-50 hover:bg-indigo-100 transition-colors text-indigo-600 font-medium text-sm"
              type="button"
              aria-expanded={whatThisSolvesExpanded}
            >
              {whatThisSolvesExpanded ? (
                <>Show less <ChevronUp className="h-4 w-4" /></>
              ) : (
                <>Learn more about tiers <ChevronDown className="h-4 w-4" /></>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* Hero Section Continued - New Tool Spotlight */}
      <section className="container mx-auto px-4 pb-10">
        <div className="max-w-4xl mx-auto">
          <button
            type="button"
            className="w-full bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 text-left hover:border-emerald-400 transition-colors"
            onClick={() => navigate('/checklist')}
            data-testid="checklist-promo-banner"
          >
            <span className="flex-1">
              <span className="flex items-center gap-2 mb-1">
                <span className="text-xs font-bold uppercase tracking-widest bg-emerald-600 text-white px-2 py-0.5 rounded">New</span>
                <span className="text-sm font-bold text-slate-800">DIY Comparison Checklist Builder</span>
              </span>
              <span className="block text-sm text-slate-600">
                Vendors: build a weighted comparison checklist. Buyers: step through features, score competitors, export to CSV.
              </span>
            </span>
            <span className="text-emerald-600 font-semibold text-sm flex-shrink-0">Try it →</span>
          </button>
        </div>
      </section>

      {/* Interactive Mind Map - Focus Mode */}
      <section className="container mx-auto px-4 py-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg p-6">
            <h2 className="text-xl font-bold text-slate-800 mb-4 text-center">Explore In Story Map Mode</h2>
            <StoryNav />
          </div>
        </div>
      </section>

      {/* Why This Matters - Hybrid Card + Accordion */}
      <section className="container mx-auto px-4 py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-md border-2 border-slate-300 overflow-hidden">
            {/* Always visible card header with summary */}
            <div className="px-6 py-5">
              <h2 className="text-2xl font-bold text-slate-900 mb-3">Why This Matters</h2>
              <p className="text-slate-600 leading-relaxed">
                Interactive stories engage visitors more deeply. With paths and decisions, your visitors don&apos;t just read content—they experience it.
              </p>
            </div>
            
            {/* Expandable details - always in DOM for SEO */}
            <div 
              className={`transition-all duration-300 ease-in-out overflow-hidden ${
                whyMattersExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
              }`}
              aria-hidden={!whyMattersExpanded}
            >
              <div className="px-6 pb-5 pt-2 border-t border-slate-100">
                <h3 className="text-xl font-semibold text-indigo-600 mb-3">Engagement Through Interactivity</h3>
                <p className="text-slate-700 leading-relaxed mb-3">
                  Traditional website builders block scripts, making it nearly impossible to embed interactive stories. TweePorter.ca solves this by restructuring your narrative into simple, safe HTML that always works.
                </p>
                <p className="text-slate-700 leading-relaxed">
                  Whether you&apos;re creating product tours, training content, or creative fiction—interactive paths keep visitors engaged longer.
                </p>
              </div>
            </div>
            
            {/* Expand/collapse button */}
            <button
              onClick={() => setWhyMattersExpanded(!whyMattersExpanded)}
              className="w-full flex items-center justify-center gap-2 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-slate-600 font-medium text-sm"
              type="button"
              aria-expanded={whyMattersExpanded}
            >
              {whyMattersExpanded ? (
                <>Show less <ChevronUp className="h-4 w-4" /></>
              ) : (
                <>Learn more <ChevronDown className="h-4 w-4" /></>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* What You Can Create */}
      <section id="what-you-can-create" className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setWhatYouCanCreateExpanded(!whatYouCanCreateExpanded)}
            className="w-full flex items-center justify-between bg-white rounded-lg shadow-md px-6 py-4 hover:shadow-lg transition-shadow border-2 border-slate-300"
            type="button"
          >
            <span className="text-2xl font-bold text-slate-900">What You Can Create</span>
            {whatYouCanCreateExpanded ? (
              <ChevronUp className="h-6 w-6 text-slate-600" />
            ) : (
              <ChevronDown className="h-6 w-6 text-slate-600" />
            )}
          </button>
          
          {whatYouCanCreateExpanded && (
            <div className="mt-4">
              <div className="grid md:grid-cols-3 gap-8">
          <div id="personal-examples" className="bg-white p-8 rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition-shadow scroll-mt-20">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
              <BookOpen className="h-6 w-6 text-indigo-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-3">Personal Stories & Creative Projects</h3>
            <p className="text-slate-600 mb-4">
              Share interactive adventures, fictional narratives, family stories, or community projects. Let readers make choices and explore multiple paths just like a classic "choose your own adventure."
            </p>
            <div className="space-y-2 pt-3 border-t border-slate-200">
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Try Examples:</p>
              <div className="flex flex-col gap-1">
                <a 
                  href="/examples/personal-the-missed-call" 
                  className="text-sm text-indigo-600 hover:text-indigo-700 hover:underline"
                >
                  → The Missed Call
                </a>
                <a 
                  href="/examples/personal-library-that-wasnt-there" 
                  className="text-sm text-indigo-600 hover:text-indigo-700 hover:underline"
                >
                  → The Library That Wasn't There
                </a>
              </div>
            </div>
          </div>

          <div id="business-examples" className="bg-white p-8 rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition-shadow scroll-mt-20">
            <a id="business-case-study-examples" className="absolute -top-20"></a>
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
              <Globe className="h-6 w-6 text-emerald-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-3">Business Case Studies That Convert</h3>
            <p className="text-slate-600 mb-3">
              Show prospects why your solution matters by letting them make decisions in the story. Demonstrate how the right path leads to better outcomes, cost savings, or avoided risks. This makes business stories far more persuasive than static text.
            </p>
            <p className="text-slate-600 mb-4">
              <strong>Businesses use interactive, decision-based stories to demonstrate ROI, train staff, and show how choosing the right path leads to measurable financial outcomes.</strong>
            </p>
            <div className="space-y-2 pt-3 border-t border-slate-200">
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Try Examples:</p>
              <div className="flex flex-col gap-1">
                <a 
                  href="/examples/business-cloud-migration-crossroads" 
                  className="text-sm text-emerald-600 hover:text-emerald-700 hover:underline"
                >
                  → Cloud Migration Crossroads
                </a>
                <a 
                  href="/examples/business-the-free-discount" 
                  className="text-sm text-emerald-600 hover:text-emerald-700 hover:underline"
                >
                  → The "Free" Discount
                </a>
              </div>
            </div>
          </div>

          <div id="training-examples" className="bg-white p-8 rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition-shadow scroll-mt-20">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
              <Lightbulb className="h-6 w-6 text-amber-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-3">Training, Education, and Onboarding</h3>
            <p className="text-slate-600 mb-4">
              Create branching scenarios where learners make decisions, see consequences instantly, and explore alternative outcomes—no LMS required. Just paste the HTML anywhere.
            </p>
            <div className="space-y-2 pt-3 border-t border-slate-200">
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Try Examples:</p>
              <div className="flex flex-col gap-1">
                <a 
                  href="/examples/training-first-day-on-the-factory-floor" 
                  className="text-sm text-amber-600 hover:text-amber-700 hover:underline"
                >
                  → First Day on the Factory Floor
                </a>
                <a 
                  href="/examples/training-inbox-training-suspicious-invoice" 
                  className="text-sm text-amber-600 hover:text-amber-700 hover:underline"
                >
                  → Inbox Training: The Suspicious Invoice
                </a>
              </div>
            </div>
          </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Compatibility */}
      <section className="container mx-auto px-4 py-12 bg-indigo-50">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-lg text-slate-700">
            <strong>Compatible with:</strong> GoDaddy, Wix, Squarespace, Shopify, Webflow, WordPress (HTML blocks), and more.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="container mx-auto px-4 py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setHowItWorksExpanded(!howItWorksExpanded)}
            className="w-full flex items-center justify-between bg-white rounded-lg shadow-md px-6 py-4 hover:shadow-lg transition-shadow border-2 border-slate-300"
            type="button"
          >
            <span className="text-2xl font-bold text-slate-900">How It Works</span>
            {howItWorksExpanded ? (
              <ChevronUp className="h-6 w-6 text-slate-600" />
            ) : (
              <ChevronDown className="h-6 w-6 text-slate-600" />
            )}
          </button>
          
          {howItWorksExpanded && (
            <div className="mt-4 bg-white rounded-lg shadow-sm p-8 border border-slate-200">
              <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Paste your interactive story code into the editor</h3>
                <p className="text-slate-600">Use our Visual Story Planner, or create your story with external tools—works with standard story formats.</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">TweePorter converts it into clean, script-free HTML</h3>
                <p className="text-slate-600">No JavaScript required—pure HTML that any website builder accepts.</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Paste the HTML into your website</h3>
                <p className="text-slate-600">Works on GoDaddy, Wix, Squarespace, Shopify, or anywhere that allows custom HTML.</p>
              </div>
            </div>
              </div>
              
              {/* Privacy Notice */}
              <div className="mt-8 bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
                <p className="text-sm text-slate-600">
                  <strong>🔒 Privacy:</strong> Exported story HTML is delivered securely by email. We never sell or share your information.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Key Features */}
      <section className="container mx-auto px-4 py-12 bg-gradient-to-b from-indigo-50 to-white">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setKeyFeaturesExpanded(!keyFeaturesExpanded)}
            className="w-full flex items-center justify-between bg-white rounded-lg shadow-md px-6 py-4 hover:shadow-lg transition-shadow border-2 border-indigo-300"
            type="button"
          >
            <span className="text-2xl font-bold text-slate-900">Key Features</span>
            {keyFeaturesExpanded ? (
              <ChevronUp className="h-6 w-6 text-indigo-600" />
            ) : (
              <ChevronDown className="h-6 w-6 text-indigo-600" />
            )}
          </button>
          
          {keyFeaturesExpanded && (
            <div className="mt-4 bg-white rounded-lg shadow-sm p-8 border border-slate-200">
              <p className="text-center text-slate-600 mb-8">Powerful tools that make interactive storytelling accessible to everyone</p>
              
              <div className="grid md:grid-cols-2 gap-6">
            {/* 7 Export Formats */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
              <div className="flex items-start space-x-3 mb-3">
                <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Globe className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">7 Export Formats</h3>
                </div>
              </div>
              <p className="text-slate-600 text-sm">
                Export your story in multiple formats: WordPress blocks, CMS packs, standalone HTML/CSS, minified versions, iframe embeds, and email-safe previews. One story, unlimited publishing options.
              </p>
            </div>

            {/* Mobile-Friendly Auto-Split */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-indigo-300 border-2 relative">
              <div className="absolute -top-2 -right-2 bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded-full">NEW</div>
              <div className="flex items-start space-x-3 mb-3">
                <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Layers className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">📏 Smart Passage Splitting</h3>
                </div>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Automatically splits long story passages into mobile-friendly chunks with "Continue..." navigation. 
                Solves the hidden content problem on restrictive CMS platforms like WordPress, Wix, and Squarespace.
              </p>
              <p className="text-indigo-700 text-xs font-semibold">
                ✨ Perfect for mobile readers and platforms with limited CSS control
              </p>
            </div>

            {/* Visual Story Planner */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
              <div className="flex items-start space-x-3 mb-3">
                <div className="flex-shrink-0 w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Network className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Visual Story Planner</h3>
                </div>
              </div>
              <p className="text-slate-600 text-sm">
                Plan complex branching narratives with our visual tree interface. Organize chapters, define story vision, and use AI assistance to generate content.
              </p>
            </div>

            {/* AI Prompting Guide */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
              <div className="flex items-start space-x-3 mb-3">
                <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">AI Story Generator Guide</h3>
                </div>
              </div>
              <p className="text-slate-600 text-sm">
                Get perfect story syntax from ChatGPT or Claude. Copy our pre-written prompting instructions to ensure your AI generates properly formatted, compiler-ready story code every time.
              </p>
            </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Why People Use TweePorter */}
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setWhyPeopleUseExpanded(!whyPeopleUseExpanded)}
            className="w-full flex items-center justify-between bg-white rounded-lg shadow-md px-6 py-4 hover:shadow-lg transition-shadow border-2 border-emerald-300"
            type="button"
          >
            <span className="text-2xl font-bold text-slate-900">Why People Use TweePorter.ca</span>
            {whyPeopleUseExpanded ? (
              <ChevronUp className="h-6 w-6 text-emerald-600" />
            ) : (
              <ChevronDown className="h-6 w-6 text-emerald-600" />
            )}
          </button>
          
          {whyPeopleUseExpanded && (
            <div className="mt-4 bg-white rounded-lg shadow-sm p-8 border border-slate-200">
              <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-3">
              <div className="text-emerald-600 text-2xl">✔</div>
              <p className="text-lg text-slate-700">Works on platforms that normally block interactive stories</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="text-emerald-600 text-2xl">✔</div>
              <p className="text-lg text-slate-700">Creates SEO-friendly, lightweight pages</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="text-emerald-600 text-2xl">✔</div>
              <p className="text-lg text-slate-700">Preserves the original storytelling structure and choices</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="text-emerald-600 text-2xl">✔</div>
              <p className="text-lg text-slate-700">Useful for hobbyists and professionals alike</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="text-emerald-600 text-2xl">✔</div>
              <p className="text-lg text-slate-700"><strong>NEW:</strong> Auto-splits long passages for mobile-friendly reading</p>
            </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Who This Is For */}
      <section className="container mx-auto px-4 py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-slate-900 mb-12 text-center">Who This Is For</h2>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-indigo-600 rounded-full"></div>
              <p className="text-lg text-slate-700">Story creators who want interactive narratives on website builders that block scripts</p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-indigo-600 rounded-full"></div>
              <p className="text-lg text-slate-700">Small businesses presenting interactive case studies</p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-indigo-600 rounded-full"></div>
              <p className="text-lg text-slate-700">Consultants demonstrating decision-based outcomes</p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-indigo-600 rounded-full"></div>
              <p className="text-lg text-slate-700">Educators building branching scenarios</p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-indigo-600 rounded-full"></div>
              <p className="text-lg text-slate-700">Anyone wanting dynamic, interactive website content</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="bg-indigo-600 rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Ready to Bring Your Interactive Stories to Life?</h2>
          <p className="text-xl mb-8 text-indigo-100">
            Create your first interactive story in minutes.
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/compiler')}
            className="bg-white text-indigo-600 hover:bg-slate-100 text-lg px-8 py-6"
          >
            Try Demo Now
          </Button>
        </div>
      </section>

      {/* FAQ Section - SEO-friendly with accordions */}
      <section className="container mx-auto px-4 py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-md border-2 border-indigo-300 overflow-hidden">
            {/* Always visible header */}
            <div className="px-6 py-5">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Frequently Asked Questions</h2>
              <p className="text-slate-600">
                Common questions about TweePorter, interactive stories, and website embedding.
              </p>
            </div>
            
            {/* FAQ Items - Always in DOM for SEO */}
            <div className="border-t border-slate-100">
              {FAQ_DATA.map((faq, index) => (
                <div key={index} className="border-b border-slate-100 last:border-b-0">
                  {/* Question - Always visible */}
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 transition-colors"
                    type="button"
                    aria-expanded={expandedFaqs[index]}
                  >
                    <span className="font-medium text-slate-900 pr-4">{faq.question}</span>
                    {expandedFaqs[index] ? (
                      <ChevronUp className="h-5 w-5 text-indigo-600 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />
                    )}
                  </button>
                  
                  {/* Answer - Always in DOM (hidden with CSS for SEO) */}
                  <div 
                    className={`transition-all duration-300 ease-in-out overflow-hidden ${
                      expandedFaqs[index] ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                    aria-hidden={!expandedFaqs[index]}
                  >
                    <div className="px-6 pb-4 text-slate-600">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Expand All / Collapse All */}
            <div className="px-6 py-3 bg-slate-50 border-t border-slate-100">
              <button
                onClick={() => {
                  const allExpanded = Object.keys(expandedFaqs).length === FAQ_DATA.length && 
                    Object.values(expandedFaqs).every(v => v);
                  const newState = {};
                  FAQ_DATA.forEach((_, i) => { newState[i] = !allExpanded; });
                  setExpandedFaqs(newState);
                }}
                className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                type="button"
              >
                {Object.keys(expandedFaqs).length === FAQ_DATA.length && 
                  Object.values(expandedFaqs).every(v => v) 
                  ? 'Collapse all' 
                  : 'Expand all answers'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-12 border-t border-slate-200">
        <div className="text-center">
          <p className="text-2xl font-semibold text-slate-900 mb-2">Interactive stories. Zero coding. Works anywhere.</p>
          <p className="text-slate-600">© 2025 TweePorter.ca Website. Built with care for storytellers.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
